import Config from '../../../../config';
import { IPwaPushSubscriptionPayload } from '../types';
import { urlBase64ToUint8Array } from './urlBase64ToUint8Array';

export interface ICreatePushSubscriptionDeps {
    registration?: ServiceWorkerRegistration | null;
    vapidPublicKey?: string;
    getUserAgent?: () => string;
}

function arrayBufferToBase64Url(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    bytes.forEach((b) => {
        binary += String.fromCharCode(b);
    });
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Ensures a Web Push subscription exists for the active service worker registration.
 */
export async function createPushSubscription(
    deps: ICreatePushSubscriptionDeps = {}
): Promise<IPwaPushSubscriptionPayload | null> {
    const vapidPublicKey = deps.vapidPublicKey ?? Config.PWA_VAPID_PUBLIC_KEY;
    const getUserAgent = deps.getUserAgent ?? (() => (typeof navigator !== 'undefined' ? navigator.userAgent : ''));

    let registration = deps.registration;
    if (!registration) {
        if (typeof navigator === 'undefined' || !navigator.serviceWorker) {
            return null;
        }
        registration = await navigator.serviceWorker.ready;
    }

    if (!registration.pushManager) {
        return null;
    }

    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
        subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
        });
    }

    const json = subscription.toJSON();
    const endpoint = json.endpoint || subscription.endpoint;
    const p256dh = json.keys?.p256dh;
    const auth = json.keys?.auth;

    if (!endpoint || !p256dh || !auth) {
        // Fallback if toJSON keys are missing (rare)
        const keyP256 = subscription.getKey('p256dh');
        const keyAuth = subscription.getKey('auth');
        if (!keyP256 || !keyAuth) {
            return null;
        }
        return {
            endpoint,
            p256dh: arrayBufferToBase64Url(keyP256),
            auth: arrayBufferToBase64Url(keyAuth),
            userAgent: getUserAgent(),
        };
    }

    return {
        endpoint,
        p256dh,
        auth,
        userAgent: getUserAgent(),
    };
}
